import React, { useState, useEffect, useMemo } from "react";
import { CheckCircle2, Circle, Zap, Target, TrendingUp, Droplets, BookOpen, Dumbbell, Moon, Sunrise, Heart, Brain, Footprints, Music, Leaf } from "lucide-react";
import Graph from "@/components/Graph";
import PageBreathingAura from "@/components/PageBreathingAura";

const CircularProgress = ({ value, size = 120, strokeWidth = 8 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const [offset, setOffset] = useState(circumference);

  useEffect(() => {
    const timer = setTimeout(() => {
      setOffset(circumference - (value / 100) * circumference);
    }, 100);
    return () => clearTimeout(timer);
  }, [value, circumference]);

  return (
    <div data-testid="circular-progress" className="relative flex items-center justify-center">
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#F59E0B"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{
            transition: 'stroke-dashoffset 0.8s ease-out',
            filter: 'drop-shadow(0 0 6px rgba(245,158,11,0.4))',
          }}
        />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span data-testid="progress-value" className="text-2xl font-bold text-white">{value}%</span>
        <span className="text-[10px] text-zinc-500 mt-0.5">Complete</span>
      </div>
    </div>
  );
};

const TASK_POOL = [
  { title: "Morning meditation", subtitle: "15 min mindfulness session", icon: Brain },
  { title: "Drink 8 glasses of water", subtitle: "Stay hydrated all day", icon: Droplets },
  { title: "Read for 20 minutes", subtitle: "Continue your current book", icon: BookOpen },
  { title: "Workout session", subtitle: "30 min strength training", icon: Dumbbell },
  { title: "Evening reflection", subtitle: "Journal your thoughts", icon: Moon },
  { title: "Sunrise walk", subtitle: "10 min outdoor walk", icon: Sunrise },
  { title: "Practice gratitude", subtitle: "Write 3 things you're grateful for", icon: Heart },
  { title: "Focus sprint", subtitle: "45 min deep work block", icon: Target },
  { title: "Stretch routine", subtitle: "10 min flexibility flow", icon: Zap },
  { title: "Track daily steps", subtitle: "Hit 8,000 steps goal", icon: Footprints },
  { title: "Review weekly trends", subtitle: "Check your progress patterns", icon: TrendingUp },
  { title: "Listen to a podcast", subtitle: "Learn something new today", icon: Music },
  { title: "Breathwork practice", subtitle: "5 min box breathing", icon: Leaf },
];

function pickRandomTasks(count) {
  const shuffled = [...TASK_POOL].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count).map((t, i) => ({
    ...t,
    id: i + 1,
    done: false,
  }));
}

export const HomeTab = () => {
  const [tasks, setTasks] = useState(() => pickRandomTasks(5));

  const doneCount = useMemo(() => tasks.filter(t => t.done).length, [tasks]);
  const progress = useMemo(() => Math.round((doneCount / tasks.length) * 100), [doneCount, tasks.length]);

  const toggleTask = (id) => {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, done: !t.done } : t));
  };

  return (
    <div
      data-testid="home-tab"
      className="flex h-full min-h-0 flex-col gap-6 overflow-y-auto px-4 pt-10 pb-24 relative z-10"
      style={{
        animation: 'fadeIn 0.4s ease-out',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <PageBreathingAura variant="home" />

      {/* Welcome */}
      <div className="relative z-10" style={{ animation: 'fadeInUp 0.5s ease-out' }}>
        <p className="text-zinc-500 text-sm tracking-wide">Welcome back</p>
        <h1 className="text-2xl font-bold tracking-tight text-white mt-1">
          Your Dashboard
        </h1>
      </div>

      {/* Progress Section */}
      <div
        data-testid="progress-section"
        className="relative z-10 flex items-center gap-6 p-5 rounded-2xl"
        style={{
          background: 'linear-gradient(180deg, rgba(18,18,18,0.92) 0%, rgba(10,10,10,0.9) 100%)',
          border: '1px solid rgba(255,255,255,0.05)',
          animation: 'fadeInUp 0.6s ease-out',
          boxShadow: '0 24px 60px rgba(0,0,0,0.22)',
          transform: 'translateZ(0)',
        }}
      >
        <CircularProgress value={progress} />
        <div className="flex-1">
          <h2 className="text-base font-semibold text-white">Daily Progress</h2>
          <p data-testid="progress-subtitle" className="text-xs text-zinc-500 mt-1">
            {doneCount} of {tasks.length} goals reached today
          </p>
          <div className="flex items-center gap-1.5 mt-3">
            <div className="h-1.5 flex-1 rounded-full bg-white/5 overflow-hidden">
              <div
                data-testid="progress-bar-fill"
                className="h-full rounded-full"
                style={{
                  width: `${progress}%`,
                  background: 'linear-gradient(90deg, #D97706, #F59E0B)',
                  transition: 'width 0.6s ease-out',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Task Cards */}
      <div className="relative z-10" style={{ animation: 'fadeInUp 0.7s ease-out' }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-zinc-400 tracking-wide">Today's Tasks</h3>
          <span data-testid="task-counter" className="text-[11px] text-zinc-600">{doneCount}/{tasks.length} done</span>
        </div>
        <div className="flex flex-col gap-2">
          {tasks.map((task) => {
            const Icon = task.icon;
            return (
              <button
                key={task.id}
                data-testid={`task-card-${task.id}`}
                onClick={() => toggleTask(task.id)}
                className="flex items-center gap-3 p-4 rounded-2xl w-full text-left"
                style={{
                  background: 'linear-gradient(180deg, rgba(16,16,16,0.94) 0%, rgba(10,10,10,0.92) 100%)',
                  border: task.done ? '1px solid rgba(245,158,11,0.15)' : '1px solid rgba(255,255,255,0.05)',
                  transition: 'border-color 0.3s ease, opacity 0.3s ease, transform 0.35s ease, box-shadow 0.35s ease',
                  WebkitTapHighlightColor: 'transparent',
                  animation: `fadeInUp 0.55s cubic-bezier(0.22, 1, 0.36, 1) ${0.12 + task.id * 0.08}s both`,
                  boxShadow: task.done
                    ? '0 18px 40px rgba(245,158,11,0.14)'
                    : '0 16px 36px rgba(0,0,0,0.18)',
                }}
              >
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: task.done ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.04)',
                    transition: 'background 0.3s ease',
                  }}
                >
                  <Icon size={16} style={{ color: task.done ? '#F59E0B' : '#71717A', transition: 'color 0.3s ease' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="text-sm font-medium"
                    style={{
                      color: task.done ? '#71717A' : '#FFFFFF',
                      textDecoration: task.done ? 'line-through' : 'none',
                      transition: 'color 0.3s ease',
                    }}
                  >
                    {task.title}
                  </p>
                  <p className="text-[11px] text-zinc-600 mt-0.5">{task.subtitle}</p>
                </div>
                <div
                  style={{
                    transform: task.done ? 'scale(1)' : 'scale(0.9)',
                    transition: 'transform 0.2s ease',
                  }}
                >
                  {task.done ? (
                    <CheckCircle2 size={20} style={{ color: '#F59E0B' }} />
                  ) : (
                    <Circle size={20} style={{ color: '#27272A' }} />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Live Graph */}
      <div className="relative z-10" style={{ animation: 'fadeInUp 0.8s ease-out' }}>
        <Graph />
      </div>
    </div>
  );
};

export default HomeTab;
